/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable import/no-unresolved */
import { useLazyQuery, useQuery } from '@apollo/client';
import { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { orderBy } from 'lodash';

import {
    GET_DIMENSIONS_DETAILS, GET_DQ_RULES, GET_DQ_ROW_DETAILS, GET_DIMENSIONS, GET_COLUMNS,
} from 'Apollo/Queries/DataRecon/DataQuality';
import {
    activeDataReconMenuVar, showDataReconMenusVar, leftNavigationItemsVar, currentLeftMenuVar,
} from 'Apollo/config';
import { sortIterate } from 'utils';
import { ModuleEnum } from 'constants';
import crossReportImg from 'assets/images/cross-report-black.svg';
import QualityDetails from '../components/QualityDetails';

const listItems = [
    [
        <i key={1} className="appkiticon icon-bar-chart-outline" style={{ fontSize: '1.5rem', color: 'white', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ fontSize: '0.7rem' }}>Data Reconciliation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-ticket-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Rule Engine</span>,
    ],
    [
        <img
            key={1}
            src={crossReportImg}
            alt="Cross Report"
            style={{
                width: '30.84px', height: '25px', top: '100px',
            }}
        />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Cross Report Validation</span>,
    ],
    [
        <i key={1} className="appkiticon icon-grow-revenue-outline" style={{ fontSize: '1.5rem', color: '#464646', top: '100px' }} />,
        <span key={2} className="a-nav-item-text" style={{ color: '#464646', fontSize: '0.7rem' }}>Trend Analytics</span>,
    ],
];

/** Columns for DQ Row Details For Each Column */
const dqDetailsColumns = [
    {
        Header: 'Row Id',
        accessor: 'rowId',
        minWidth: 100,
        resizable: true,
    },
    {
        Header: 'Rule Name',
        accessor: 'ruleName',
        minWidth: 150,
        resizable: true,
    },
    {
        Header: 'Rule Description',
        accessor: 'ruleDescription',
        minWidth: 150,
    },
    {
        Header: 'Column Value',
        accessor: 'value',
        minWidth: 90,
        resizable: true,
    },
];

const QualityDetailsContainer = () => {
    const history = useHistory();
    const location = useLocation();
    const [showDetailsTable, setShowDetailsTable] = useState(false);
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [showTableLoader, setShowTableLoader] = useState(false);
    const [detailsTableData, setDetailsTableData] = useState([]);
    const [isRowDetailsError, setIsRowDetailsError] = useState(false);
    const [isDimensionDetailsError, setIsDimensionDetailsError] = useState(false);
    const [selectedDimensionId, setSelectedDimensionId] = useState([].concat(location.state.dimentionId));
    const [selectedColumnDimensionId, setSelectedColumnDimensionId] = useState(null);
    const [selectedColumnId, setSelectedColumnId] = useState(null);
    const [selectedColumnDimensionName, setSelectedColumnDimensionName] = useState(null);

    /* apollo query to fetch data quality rules */
    const [getDQRules, { data: rulesData, error: rulesError }] = useLazyQuery(GET_DQ_RULES, {
        variables: {
            projectId: parseInt(JSON.parse(location.state.project.projectId), 10),
        },
    });

    /* apollo query to fetch data quality summary details */
    const [getDQDimensionDetails, { data: dimensionDetails, error: dimensionDetailsError, loading: dimensionDetailsLoading }] = useLazyQuery(GET_DIMENSIONS_DETAILS, {
        fetchPolicy: 'no-cache',
        variables: {
            fileName: location.state.file,
            dimensionIds: selectedDimensionId,
            columnIds: selectedColumnId,
        },
    });

    /* Apollo query to fetch columns list  */
    const { data: columnsList } = useQuery(GET_COLUMNS, {
        variables: {
            fileName: location.state.file,
        },
        fetchPolicy: 'no-cache',
    });

    /* Apollo query to fetch row details  */
    const [getRowDetails, { data: rowDetailsData, error: rowDetailsError }] = useLazyQuery(GET_DQ_ROW_DETAILS, {
        fetchPolicy: 'no-cache',
        variables: {
            fileName: location.state.file,
            dimensionId: parseInt(selectedColumnDimensionId, 10),
            columnName: selectedColumn,
        },
    });

    /* Apollo query to fetch dimensions  */
    const dimensions = useQuery(GET_DIMENSIONS, {
        fetchPolicy: 'no-cache',
    });

    const filteredDimensions = dimensions.data && dimensions.data.DQgetDimensions
        .filter(d => location.state.dimScore
            .some(element => element.title === d.dimensionName && element.value !== null));

    /* function to navigate to projects */
    const navigateToProjects = () => history.push({
        pathname: '/data-recon-home',
        state: {
            tenantId: location.state.tenantId,
        },
    });

    /* function to navigate to Data Recon home page */
    const navigateToReconHome = () => history.push(
        {
            pathname: '/data-recon-home',
            state: {
                tenantId: location.state.tenantId,
            },
        },
    );

    /* function to navigate to file upload page/ project details page */
    const navigateToDataLineageUpload = () => history.push({
        pathname: '/data-lineage-upload',
        state: {
            project: parseInt(location.state.project.projectId, 10),
            tenantId: location.state.tenantId,
        },
    });

    /* function to navigate to back */
    const navigateBack = () => {
        const encodedFileName = encodeURIComponent(location.state.file);
        const encodedTenantId = encodeURIComponent(location.state.tenantId);
        const encodedProject = encodeURIComponent(JSON.stringify(location.state.project));
        const encodedFileDisplayName = encodeURIComponent(location.state.fileDisplayName);
        history.push({
            pathname: '/data-quality',
            search: `?file=${encodedFileName}&tenantId=${encodedTenantId}&project=${encodedProject}&fileDisplayName=${encodedFileDisplayName}`,
        });
    };

    /* function called when dimension dropdown is closed */
    const onDimensionDropDownClose = dimensionIds => {
        setSelectedDimensionId(dimensionIds);
    };

    /* function called when column dropdown is closed */
    const onColumnDropDownClose = columnIds => {
        setSelectedColumnId(columnIds);
    };

    /* function called when a column chart is selected */
    const onColumnBarSelection = e => {
        setSelectedColumn(e.point.options.name);
        setSelectedColumnDimensionId(filteredDimensions.filter(dm => dm.dimensionName === e.point.series.name).map(val => val.dimensionId)[0]);
        setSelectedColumnDimensionName(filteredDimensions.filter(dm => dm.dimensionName === e.point.series.name).map(val => val.dimensionName)[0]);
    };

    /* side effect called when component loads */
    useEffect(() => {
        leftNavigationItemsVar(listItems);
        currentLeftMenuVar(ModuleEnum.DATARECONCILIATION);
        getDQDimensionDetails();
    }, []);

    /* side effect called when selectedDimensionId is changed */
    useEffect(() => {
        if (selectedDimensionId && (dimensions.data && dimensions.data.DQgetDimensions)) {
            setShowDetailsTable(false);
        }
    }, [selectedDimensionId]);

    /* side effect when dimensionDetailsError is available, set the isDimensionDetailsError state variable. */
    useEffect(() => {
        if (dimensionDetailsError) {
            setIsDimensionDetailsError(true);
        } else {
            setIsDimensionDetailsError(false);
        }
        showDataReconMenusVar(true);
        activeDataReconMenuVar('projects');
    }, [dimensionDetailsError]);

    /* side effect when column is selected from bar chart, fetch row details for the
    * selected column and all DQ rules.
    * Set loader to show table data is loading
    * Set showDetailsTable state variable to show the Row Details panel
     */
    useEffect(() => {
        if (selectedColumn) {
            Promise.all([getDQRules(), getRowDetails()]);
            setShowTableLoader(true);
            setShowDetailsTable(true);
        }
    }, [selectedColumn, selectedColumnDimensionId]);

    /* side effect when columnsList is available, set the selectedColumnId state variable. */
    useEffect(() => {
        if (columnsList && columnsList.DQgetColumns) {
            const colIds = columnsList.DQgetColumns.map(c => c.columnId);
            setSelectedColumnId(colIds);
        }
    }, [columnsList]);

    /* side effect when rowDetailsData and rulesData is available, format the data and set the data to state variable. */
    useEffect(() => {
        if (rowDetailsData && rowDetailsData.DQgetRowDetails && rulesData && rulesData.DQgetRules) {
            const tableData = rowDetailsData.DQgetRowDetails.map(d => {
                const ruleDefinition = rulesData.DQgetRules.find(r => r.ruleId === d.ruleId);
                return {
                    ...d,
                    ruleName: ruleDefinition.ruleName,
                    ruleDescription: ruleDefinition.ruleDescription,
                };
            });
            const orderedTableData = orderBy(tableData, [item => sortIterate(item, 'value')], ['asc']);
            setDetailsTableData(orderedTableData);
            setShowTableLoader(false);
        }
    }, [rowDetailsData, rulesData]);

    /* side effect when rowDetailsError and rulesError is available, set the isRowDetailsError state variable. */
    useEffect(() => {
        if (rowDetailsError || rulesError) {
            setIsRowDetailsError(true);
        } else {
            setIsRowDetailsError(false);
        }
    }, [rowDetailsError, rulesError]);

    // Breadcrumbs items
    const items = [
        {
            link: '',
            value: <span className="appkiticon icon-home-fill font-16" onClick={navigateToReconHome} role="presentation" />,
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={navigateToProjects} role="presentation"> Projects </span>,
        },
        {
            link: '',
            value: (
                <span
                    className="a-breadcrumb-item-text"
                    onClick={navigateToDataLineageUpload}
                    role="presentation"
                >
                    {location.state.project && location.state.project.projectName}
                </span>
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text" onClick={() => history.goBack()} role="presentation">DQS Dashboard</span>,
        },
        {
            link: '',
            value: <span className="a-h6">Data Quality Details</span>,
        },
    ];

    return (
        <QualityDetails
            breadcrumbItems={items}
            navigateBack={navigateBack}
            columnWiseDQScore={dimensionDetails && dimensionDetails.DQgetDimensionDetails}
            showDetailsTable={showDetailsTable}
            setShowDetailsTable={setShowDetailsTable}
            dimensionDetailsLoading={dimensionDetailsLoading}
            rowsProcesed={location.state.rowsProcessed}
            dataQualityDetailsColumns={dqDetailsColumns}
            showTableLoader={showTableLoader}
            detailsTableData={detailsTableData}
            selectedColumn={selectedColumn}
            isRowDetailsError={isRowDetailsError}
            isDimensionDetailsError={isDimensionDetailsError}
            dimensions={filteredDimensions}
            selectedDimensionId={selectedDimensionId}
            setSelectedDimensionId={setSelectedDimensionId}
            selectedColumnDimensionName={selectedColumnDimensionName}
            columnsList={columnsList && columnsList.DQgetColumns}
            selectedColumnId={selectedColumnId}
            setSelectedColumnId={setSelectedColumnId}
            onDimensionDropDownClose={onDimensionDropDownClose}
            onColumnDropDownClose={onColumnDropDownClose}
            onColumnBarSelection={onColumnBarSelection}
        />
    );
};

export default QualityDetailsContainer;
